import { useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetEdgeCustomerLookupQuery } from "../store/api";
import { getErrorMessage } from "../utils/errorMessage";

const DEFAULT_DEBOUNCE_MS = 400;
const DEFAULT_MIN_DIGITS = 8;

const getDigits = (value) => String(value || "").replace(/\D/g, "");

export const useCustomerLookup = ({
  phone,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minDigits = DEFAULT_MIN_DIGITS,
} = {}) => {
  const [triggerLookup] = useLazyGetEdgeCustomerLookupQuery();
  const [status, setStatus] = useState("idle");
  const [customer, setCustomer] = useState(null);
  const [customerPhone, setCustomerPhone] = useState("");
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const normalizedPhone = useMemo(() => String(phone || "").trim(), [phone]);
  const digitCount = useMemo(() => getDigits(normalizedPhone).length, [normalizedPhone]);
  const canLookup = enabled && digitCount >= minDigits;
  const currentCustomer =
    customerPhone === normalizedPhone ? customer : null;

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled) {
      setStatus("idle");
      setCustomer(null);
      setCustomerPhone("");
      setError(null);
      return undefined;
    }

    if (!normalizedPhone || digitCount < minDigits) {
      setStatus("idle");
      setCustomer(null);
      setCustomerPhone("");
      setError(null);
      return undefined;
    }

    setStatus("searching");
    setCustomer(null);
    setCustomerPhone("");
    setError(null);

    const timer = setTimeout(async () => {
      try {
        const result = await triggerLookup(normalizedPhone, true).unwrap();
        if (requestIdRef.current !== requestId) return;
        setCustomer(result || null);
        setCustomerPhone(normalizedPhone);
        setStatus(result ? "found" : "new");
      } catch (err) {
        if (requestIdRef.current !== requestId) return;
        setCustomer(null);
        setCustomerPhone("");
        setStatus("error");
        setError(getErrorMessage(err, "Unable to lookup customer right now."));
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [enabled, normalizedPhone, digitCount, minDigits, debounceMs, triggerLookup]);

  return {
    status,
    customer: currentCustomer,
    error,
    canLookup,
    isSearching: status === "searching",
    isFound: status === "found",
    isNewCustomer: status === "new",
  };
};
